import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Upload } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { useToast } from 'hooks/use-toast';
import { useImportMcpServers } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import type { IImportMcpServerEntry } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { getApiErrorDetail } from 'utils/api-error';

type ImportMcpServersModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectKey: string;
};

const PLACEHOLDER = `{
  "mcpServers": {
    "my-server": {
      "url": "https://example.com/mcp",
      "headers": { "Authorization": "Bearer <token>" }
    }
  }
}`;

export function ImportMcpServersModal({ open, onOpenChange, projectKey }: ImportMcpServersModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync: importMcpServers, isPending } = useImportMcpServers();

  const [content, setContent] = useState('');
  const [results, setResults] = useState<IImportMcpServerEntry[] | null>(null);

  useEffect(() => {
    if (!open) {
      setContent('');
      setResults(null);
    }
  }, [open]);

  const handleFile = async (file?: File | null) => {
    if (!file) return;
    setContent(await file.text());
  };

  const handleImport = async () => {
    try {
      const response = await importMcpServers({
        content,
        project_key: projectKey,
        is_open_tool: false,
      });

      if (!response?.results) {
        toast({
          variant: 'destructive',
          title: t('IMPORT_FAILED', 'Import failed'),
          description:
            response?.detail ||
            response?.message ||
            t('IMPORT_MCP_SERVERS_FAILED_DESCRIPTION',
            'The JSON could not be imported.'),
        });
        return;
      }

      setResults(response.results);

      if (response.created > 0) {
        await queryClient.invalidateQueries({ queryKey: ['tools'] });
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('IMPORT_FAILED', 'Import failed'),
        description:
          getApiErrorDetail(error) ??
          t('IMPORT_MCP_SERVERS_FAILED_DESCRIPTION', 'The JSON could not be imported.'),
      });
    }
  };

  const created = results?.filter((entry) => entry.status === 'created') ?? [];
  const rejected = results?.filter((entry) => entry.status === 'rejected') ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{t('IMPORT_FROM_JSON', 'Import from JSON')}</DialogTitle>
          <DialogDescription>
            {t('IMPORT_FROM_JSON_DESCRIPTION',
              'Paste a Claude-style mcpServers block. Every remote server becomes its own MCP tool. Local servers that run a command are not supported.')}
          </DialogDescription>
        </DialogHeader>

        {results === null ? (
          <div className="flex flex-col gap-2 overflow-y-auto">
            <div className="flex items-center justify-between">
              <Label htmlFor="mcp-servers-json">{t('CONFIGURATION', 'Configuration')}</Label>
              <label className="inline-flex cursor-pointer items-center gap-1.5 text-sm text-primary">
                <Upload className="h-4 w-4" />
                {t('UPLOAD_FILE', 'Upload file')}
                <input
                  type="file"
                  accept=".json,application/json"
                  className="hidden"
                  onChange={(event) => handleFile(event.target.files?.[0])}
                />
              </label>
            </div>
            <Textarea
              id="mcp-servers-json"
              value={content}
              onChange={(event) => setContent(event.target.value)}
              className="min-h-[280px] resize-none font-mono text-sm"
              spellCheck={false}
              placeholder={PLACEHOLDER}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-3 overflow-y-auto">
            <div className="flex items-center gap-2">
              {created.length > 0 ? (
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-destructive" />
              )}
              <p className="text-sm font-medium">
                {t('IMPORT_MCP_SERVERS_SUMMARY', {
                  created: created.length,
                  rejected: rejected.length,
                  defaultValue: '{{created}} added, {{rejected}} skipped',
                })}
              </p>
            </div>
            <ul className="divide-y divide-border rounded-lg border border-border">
              {results.map((entry, index) => (
                <li key={`${entry.name}-${index}`} className="flex flex-col gap-1 p-3">
                  <div className="flex items-center gap-2">
                    {entry.status === 'created' ? (
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600 dark:text-green-400" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
                    )}
                    <span className="text-sm font-medium">{entry.name}</span>
                    {entry.server_url ? (
                      <span className="truncate text-xs text-muted-foreground">{entry.server_url}</span>
                    ) : null}
                  </div>
                  {entry.reason ? (
                    <p className="pl-6 text-xs text-muted-foreground">{entry.reason}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter className="gap-2">
          {results === null ? (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
                {t('CANCEL', 'Cancel')}
              </Button>
              <Button onClick={handleImport} disabled={!content.trim() || isPending}>
                {isPending ? t('IMPORTING', 'Importing...') : t('IMPORT', 'Import')}
              </Button>
            </>
          ) : (
            <Button onClick={() => onOpenChange(false)}>{t('DONE', 'Done')}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
